import { useCallback, useEffect, useState } from "react";
import { fetchBoard } from "@/lib/ccApi";

// ── Pipeline boards (Phase 2, JOB-20261023-OS-PIPEVIZ) ───────────────────────
// One fetch pass for the boards the PIPELINE strip reads:
//   projects  → the NO OWNER and SET ASIDE rails + the kit-now card
//   kitasks   → the kit-asks behind the WITH YOU rail
//   goals     → the goals behind the WITH YOU rail
//   breakage  → the watches behind the WITH YOU rail
//   mail      → the messages behind the WITH YOU rail
//   lanes     → the KIT + LANES rail
//   work      → the board behind the goal axis on the same page, fetched in
//               the same pass so the page reads one moment of truth
// All seven go out in parallel (Promise.all). One board failing never takes
// the others down: each settles on its own { ok, items, meta } and the rail
// that depends on it degrades to "—".
//
// The pattern mirrors the page hook (useBoard → ccApi.fetchBoard): live from
// the API when reachable, plain-English failure states otherwise. There is no
// baked fallback here — a second truth on this surface is how two surfaces
// end up disagreeing (council F4).
//
// `enabled` is false when the host page already owns the fetch and hands the
// boards down; the hook then stays out of the network entirely.

const BOARDS = ["projects", "work", "kitasks", "lanes", "goals", "breakage", "mail"];

const loadBoard = async (name) => {
  try {
    const body = await fetchBoard(name);
    return {
      ok: true,
      items: Array.isArray(body?.items) ? body.items : [],
      meta: body?.meta && typeof body.meta === "object" ? body.meta : null,
    };
  } catch {
    return { ok: false, items: [], meta: null };
  }
};

export function usePipelineBoards(enabled = true) {
  const [boards, setBoards] = useState(null); // null → loading (skeleton frames)

  const load = useCallback(async () => {
    const settled = await Promise.all(BOARDS.map(loadBoard));
    const next = {};
    BOARDS.forEach((name, i) => {
      next[name] = settled[i];
    });
    setBoards(next);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return undefined;
  }, [enabled, load]);

  // Keep it current: refresh when Jeff comes back to the tab (cheap, no sockets).
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled, load]);

  return { boards, loading: boards === null, refresh: load };
}

export default usePipelineBoards;
